import {
  ArrowUpIcon,
  CheckCircleIcon,
  ClockIcon,
  CloudCheckIcon,
  CrownIcon,
  SealWarningIcon,
  StarIcon,
  TrophyIcon,
} from '@phosphor-icons/react';

/*
  Product mockups for the public website, drawn in HTML and CSS (no screenshots, so they stay
  sharp and light). The sample names and numbers are illustrative.
*/

export function PhoneExamMockup() {
  const options = ['120 किमी', '150 किमी', '180 किमी', '200 किमी'];
  return (
    <div className="mk-phone">
      <div className="mk-phone__screen">
        <div className="mk-phone__island" />
        <div className="mk-exam-top">
          <div style={{ minWidth: 0 }}>
            <div className="mk-exam-title">SSC CGL Mock 12</div>
            <div className="mk-exam-saved">
              <CheckCircleIcon size={11} weight="fill" />
              सेव हो गया
            </div>
          </div>
          <div className="mk-timer">
            <ClockIcon size={13} weight="bold" />
            38:12
          </div>
        </div>
        <div className="mk-sections">
          <span data-active="true">गणित</span>
          <span>तर्कशक्ति</span>
          <span>GK</span>
          <span>English</span>
        </div>
        <div className="mk-body">
          <div className="mk-qhead">
            <span>प्रश्न 7 / 100</span>
            <span className="mk-marks">
              <i className="g">+2</i>
              <i className="r">−0.5</i>
            </span>
          </div>
          <div className="mk-question">एक ट्रेन 60 किमी/घंटा की चाल से चलती है। 3 घंटे में वह कितनी दूरी तय करेगी?</div>
          {options.map((o, i) => (
            <div key={o} className="mk-option" data-selected={i === 2}>
              <span>{'ABCD'[i]}</span>
              {o}
            </div>
          ))}
        </div>
        <div className="mk-actions">
          <span className="mk-btn mk-btn--violet">चिह्नित करें</span>
          <span className="mk-btn mk-btn--brand">सेव करें और आगे</span>
        </div>
      </div>
    </div>
  );
}

function SavedToast() {
  return (
    <div className="mk-card mk-toast">
      <span className="mk-icon mk-icon--green">
        <CloudCheckIcon size={20} weight="duotone" />
      </span>
      <div>
        <div className="mk-strong">Answer saved</div>
        <div className="mk-muted">Even on a weak network</div>
      </div>
    </div>
  );
}

function LanguageChip() {
  return (
    <div className="mk-card mk-lang">
      <div className="mk-seg">
        <span data-active="true">हिं</span>
        <span>EN</span>
      </div>
      <div>
        <div className="mk-strong">हिंदी + English</div>
        <div className="mk-muted">Switch any time</div>
      </div>
    </div>
  );
}

function ResultCard() {
  return (
    <div className="mk-card mk-result">
      <span className="mk-badge">
        <CrownIcon size={13} weight="fill" />
        Top 10
      </span>
      <div className="mk-result__head">
        <span className="mk-icon mk-icon--saffron">
          <TrophyIcon size={20} weight="duotone" />
        </span>
        <div>
          <div className="mk-muted">SSC CGL Mock 12</div>
          <div className="mk-strong">Your result</div>
        </div>
      </div>
      <div className="mk-result__rank">
        <b>#3</b>
        <span>of 214</span>
      </div>
      <div className="mk-result__stats">
        <div>
          <span>Percentile</span>
          <b>98.6</b>
        </div>
        <div>
          <span>Score</span>
          <b>162.5</b>
        </div>
      </div>
      <div className="mk-bar">
        <i className="g" style={{ width: '72%' }} />
        <i className="r" style={{ width: '11%' }} />
        <i className="n" style={{ width: '17%' }} />
      </div>
    </div>
  );
}

/** Hero: the exam on a phone, with the moments that matter around it. */
export function HeroVisual() {
  return (
    <div className="lp-stage" aria-hidden="true">
      <div className="lp-stage__glow" />
      <PhoneExamMockup />
      <SavedToast />
      <ResultCard />
      <LanguageChip />
    </div>
  );
}

function WindowFrame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mk-window" aria-hidden="true">
      <div className="mk-window__bar">
        <span className="mk-window__dots">
          <i />
          <i />
          <i />
        </span>
        <span className="mk-window__title">{title}</span>
      </div>
      <div className="mk-window__body">{children}</div>
    </div>
  );
}

const PROGRESS = [58, 64, 61, 72, 80, 91];

function ProgressChart() {
  const w = 300;
  const h = 120;
  const pad = { l: 8, r: 18, t: 14, b: 22 };
  const x = (i: number) => pad.l + (i * (w - pad.l - pad.r)) / (PROGRESS.length - 1);
  // The y axis spans percentile 50 (bottom) to 100 (top).
  const scaleY = (v: number) => pad.t + ((100 - v) / 50) * (h - pad.t - pad.b);
  const points = PROGRESS.map((v, i) => `${x(i).toFixed(1)},${scaleY(v).toFixed(1)}`).join(' ');
  const area = `${pad.l},${h - pad.b} ${points} ${x(PROGRESS.length - 1).toFixed(1)},${h - pad.b}`;
  const last = PROGRESS.length - 1;
  return (
    <svg className="mk-chart" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Percentile rising across six tests">
      <defs>
        <linearGradient id="mk-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3646A5" stopOpacity="0.22" />
          <stop offset="1" stopColor="#3646A5" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 1, 2].map((i) => (
        <line key={i} x1={pad.l} x2={w - pad.r} y1={pad.t + (i * (h - pad.t - pad.b)) / 2} y2={pad.t + (i * (h - pad.t - pad.b)) / 2} stroke="#EEF2F7" strokeWidth="1" />
      ))}
      <polygon points={area} fill="url(#mk-area)" />
      <polyline points={points} fill="none" stroke="#3646A5" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {PROGRESS.map((v, i) => (
        <circle key={i} cx={x(i)} cy={scaleY(v)} r={i === last ? 5 : 3} fill={i === last ? '#FF9800' : '#fff'} stroke={i === last ? '#fff' : '#3646A5'} strokeWidth="2" />
      ))}
      {PROGRESS.map((_, i) => (
        <text key={i} x={x(i)} y={h - 5} textAnchor="middle" fontSize="10" fill="#94A3B8" fontWeight="600">
          T{i + 1}
        </text>
      ))}
    </svg>
  );
}

function Topic({ name, value, color }: { name: string; value: number; color: string }) {
  return (
    <div className="mk-topic">
      <span>{name}</span>
      <span className="mk-topic__track">
        <i style={{ width: `${value}%`, background: color }} />
      </span>
      <b>{value}%</b>
    </div>
  );
}

/** For students and parents: a result, progress and topics. */
export function StudentsVisual() {
  return (
    <WindowFrame title="Result · SSC CGL Mock 12">
      <div className="mk-result-wide">
        <div>
          <div className="mk-result-wide__label">Your rank</div>
          <div className="mk-result-wide__rank">
            <b>#3</b>
            <span>of 214</span>
          </div>
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="mk-result-wide__stats">
            <div>
              <span>Score</span>
              <b>162.5</b>
            </div>
            <div>
              <span>Percentile</span>
              <b>98.6</b>
            </div>
            <div>
              <span>Accuracy</span>
              <b>91%</b>
            </div>
          </div>
          <div className="mk-badges">
            <span>
              <CrownIcon size={13} weight="fill" color="#FFC05C" />
              Top 10
            </span>
            <span>
              <ArrowUpIcon size={13} weight="bold" color="#86EFAC" />
              Up 4 ranks
            </span>
            <span>
              <StarIcon size={13} weight="fill" color="#FFC05C" />
              Personal best
            </span>
          </div>
        </div>
      </div>
      <div className="mk-grid-2">
        <div className="mk-panel">
          <div className="mk-panel__head">
            <span className="mk-panel__title">Percentile, last 6 tests</span>
            <span className="mk-tag mk-tag--green">+33</span>
          </div>
          <ProgressChart />
        </div>
        <div className="mk-panel">
          <div className="mk-panel__head">
            <span className="mk-panel__title">Topic accuracy</span>
          </div>
          <Topic name="Maths" value={86} color="#16A34A" />
          <Topic name="Reasoning" value={78} color="#3646A5" />
          <Topic name="English" value={64} color="#0288D1" />
          <Topic name="GK" value={41} color="#F28B00" />
        </div>
      </div>
    </WindowFrame>
  );
}

function Person({ initials, name, detail, tag, tone }: { initials: string; name: string; detail: string; tag: string; tone: 'red' | 'saffron' }) {
  return (
    <div className="mk-row">
      <span className="mk-avatar">{initials}</span>
      <span className="mk-row__text">
        <b>{name}</b>
        <span>{detail}</span>
      </span>
      <span className={`mk-tag mk-tag--${tone}`}>{tag}</span>
    </div>
  );
}

/** For teachers: live counts, students who need a push, and an answer-key check. */
export function TeachersVisual() {
  return (
    <WindowFrame title="Dashboard · Demo Coaching Centre">
      <div className="mk-stats">
        <div className="mk-stat">
          <span>Writing now</span>
          <b>
            <i className="mk-live" />
            46
          </b>
        </div>
        <div className="mk-stat">
          <span>Submitted</span>
          <b>128</b>
        </div>
        <div className="mk-stat">
          <span>Average score</span>
          <b>61%</b>
        </div>
      </div>
      <div className="mk-alert">
        <span className="mk-icon mk-icon--red">
          <SealWarningIcon size={20} weight="duotone" />
        </span>
        <span className="mk-row__text">
          <b>Q 14 may have a wrong answer key</b>
          <span>Most top scorers chose option B</span>
        </span>
        <span className="mk-alert__btn">Fix key</span>
      </div>
      <div className="mk-grid-2">
        <div className="mk-panel">
          <div className="mk-panel__head">
            <span className="mk-panel__title">Needs attention</span>
          </div>
          <div className="mk-rows">
            <Person initials="AK" name="Aman Kumar" detail="Missed 2 of the last 3 tests" tag="Missed" tone="red" />
            <Person initials="PS" name="Priya Singh" detail="Percentile fell from 82 to 64" tag="Falling" tone="saffron" />
            <Person initials="RY" name="Rahul Yadav" detail="Bottom 20% in the latest test" tag="Low" tone="saffron" />
          </div>
        </div>
        <div className="mk-panel">
          <div className="mk-panel__head">
            <span className="mk-panel__title">Batch accuracy</span>
          </div>
          <Topic name="Maths" value={71} color="#16A34A" />
          <Topic name="Reasoning" value={78} color="#3646A5" />
          <Topic name="English" value={64} color="#0288D1" />
          <Topic name="GK" value={52} color="#F28B00" />
        </div>
      </div>
    </WindowFrame>
  );
}
